import { useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-image-2-line',
    title: 'Share Your Vision',
    description:
      'Upload inspiration photos of your dream wedding dress. Fell in love with a $14,000 wedding dress but don\'t have the budget? Dreamline is here to help — we will bring your dream dress to life, tailored to your measurements.',
    image: "https://public.readdy.ai/ai/img_res/1f002f09d273bcec8e7d4736afd5db3a.jpg",
  },
  {
    number: '02',
    icon: 'ri-customer-service-2-line',
    title: 'Design Consultation',
    description:
      'Our design team reviews your inspiration and confirms what\'s possible. Then we jump on a 30-minute call to plan every detail and review timelines.',
    image: "https://readdy.ai/api/search-image?query=elegant%20fashion%20designer%20sitting%20at%20a%20desk%20with%20fabric%20samples%20sketching%20a%20wedding%20dress%20design%20with%20pencil%20wearing%20white%20shirt%20in%20a%20bright%20modern%20atelier%20studio%20minimal%20clean%20workspace&width=480&height=560&seq=step2&orientation=portrait",
  },
  {
    number: '03',
    icon: 'ri-scissors-line',
    title: 'Crafted With Care',
    description:
      'We take exact measurements over a video call with you, and begin handcrafting your gown. You\'ll receive photo updates along the way so you can watch your dress come to life.',
    image: "https://readdy.ai/api/search-image?query=professional%20seamstress%20carefully%20sewing%20delicate%20white%20silk%20wedding%20dress%20fabric%20by%20hand%20in%20a%20luxury%20atelier%20close%20up%20of%20hands%20and%20white%20fabric%20cream%20threads%20gold%20scissors%20embroidery%20elegant%20craftsmanship&width=480&height=560&seq=step3&orientation=portrait",
  },
  {
    number: '04',
    icon: 'ri-gift-2-line',
    title: 'Delivered to You',
    description:
      'Your finished masterpiece arrives in luxury packaging, carefully preserved for your big day. Worldwide shipping, fully insured, with white-glove handling every step of the way.',
    image: "https://readdy.ai/api/search-image?query=beautiful%20luxury%20white%20bridal%20dress%20box%20packaging%20tied%20with%20satin%20ribbon%20being%20opened%20to%20reveal%20white%20silk%20wedding%20gown%20tissue%20paper%20wrapped%20elegant%20unboxing%20experience%20cream%20champagne%20tones&width=480&height=560&seq=step4&orientation=portrait",
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('[data-animate]').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="py-28 px-6 lg:px-12" style={{ background: '#FAF7F3' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20" data-animate style={{ opacity: 0, transform: 'translateY(24px)', transition: 'all 0.7s ease' }}>
          <p
            className="inline-block text-xs tracking-widest uppercase px-4 py-1.5 border border-amber-300/60 text-amber-700 mb-6 rounded-sm"
            style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.18em', fontSize: '0.65rem' }}
          >
            The Journey
          </p>
          <h2
            className="text-4xl lg:text-5xl text-stone-800 mb-5"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, lineHeight: 1.15 }}
          >
            From Dream to Reality
          </h2>
          <p
            className="text-stone-500 max-w-md mx-auto leading-relaxed"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            We recommend starting your Dreamline journey 5 months before your wedding date to ensure every detail is perfected.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={step.number}
              data-animate
              className="bg-white rounded-lg overflow-hidden cursor-default group"
              style={{
                opacity: 0,
                transform: 'translateY(32px)',
                transition: `all 0.7s ease ${index * 0.1}s`,
              }}
            >
              {/* Image */}
              <div className="w-full h-64 overflow-hidden">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {/* Content */}
              <div className="p-7">
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="text-3xl text-amber-600/40"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
                  >
                    {step.number}
                  </span>
                  <div className="w-px h-6 bg-stone-200" />
                  <i className={`${step.icon} text-amber-600/70`} style={{ fontSize: '1.1rem' }} />
                </div>
                <h3
                  className="text-lg text-stone-800 mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-sm text-stone-500 leading-relaxed"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                >
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Timeline note */}
        <div
          className="mt-16 text-center"
          data-animate
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'all 0.7s ease 0.5s' }}
        >
          <div className="inline-flex items-center gap-3 bg-amber-50 border border-amber-200/60 px-8 py-4 rounded-sm">
            <i className="ri-time-line text-amber-600" style={{ fontSize: '1rem' }} />
            <p
              className="text-sm text-stone-600"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
            >
              <strong className="text-stone-800 font-medium">We recommend 5 months</strong> — the earlier you start, the more time we have to perfect every detail.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
